/**
 * The diagram zoom view's state: whether it is open, what it shows, and how
 * far it is zoomed and panned. The view draws these; nothing here touches the
 * page but the document's scroll lock while the view is open.
 */
import { Signal } from '@crown/signal';

const MIN_SCALE = 0.08;
const MAX_SCALE = 16;
export const ZOOM_FACTOR = 1.08;
export const ZOOM_STEP = 0.15;
const PAN_STEP = 40;
/** How long the view takes to close, after which what it showed is let go. */
const CLOSE_MS = 250;

export type ZoomContent =
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly message: string }
  | { readonly state: 'ready'; readonly svg: SVGElement };

export class ZoomModel {
  readonly open = new Signal.State(false);
  readonly label = new Signal.State('Diagram');
  readonly content = new Signal.State<ZoomContent | null>(null);
  readonly scale = new Signal.State(1);
  readonly tx = new Signal.State(0);
  readonly ty = new Signal.State(0);
  readonly dragging = new Signal.State(false);

  readonly transform = new Signal.Computed(() => `translate(${this.tx.get()}px, ${this.ty.get()}px) scale(${this.scale.get()})`);

  private last = { x: 0, y: 0 };
  private pinch = 0;
  private request = 0;

  reset(): void {
    this.scale.set(1);
    this.tx.set(0);
    this.ty.set(0);
  }

  /** Zooms by `factor` about the point (cx, cy) from the viewport's centre, within the scale limits. */
  zoomBy(factor: number, cx = 0, cy = 0): void {
    const scale = this.scale.get();
    const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * factor));
    if (next === scale) return;
    const ratio = next / scale;
    this.tx.set(cx + (this.tx.get() - cx) * ratio);
    this.ty.set(cy + (this.ty.get() - cy) * ratio);
    this.scale.set(next);
  }

  panBy(dx: number, dy: number): void {
    this.tx.set(this.tx.get() + dx);
    this.ty.set(this.ty.get() + dy);
  }

  /** The keys the view answers: Escape, + and -, 0, and the arrows. */
  key(event: KeyboardEvent): void {
    const actions: Record<string, () => void> = {
      Escape: () => this.close(),
      '+': () => this.zoomBy(1 + ZOOM_STEP),
      '=': () => this.zoomBy(1 + ZOOM_STEP),
      '-': () => this.zoomBy(1 - ZOOM_STEP),
      '0': () => this.reset(),
      ArrowLeft: () => this.panBy(-PAN_STEP, 0),
      ArrowRight: () => this.panBy(PAN_STEP, 0),
      ArrowUp: () => this.panBy(0, -PAN_STEP),
      ArrowDown: () => this.panBy(0, PAN_STEP),
    };
    actions[event.key]?.();
  }

  startDrag(x: number, y: number): void {
    this.dragging.set(true);
    this.last = { x, y };
    this.pinch = 0;
  }

  drag(x: number, y: number): void {
    if (!this.dragging.get()) return;
    this.panBy(x - this.last.x, y - this.last.y);
    this.last = { x, y };
  }

  endDrag(): void {
    this.dragging.set(false);
    this.pinch = 0;
  }

  /** Two fingers: zoom by how far apart they have moved since the last reading. */
  pinchTo(distance: number): void {
    this.dragging.set(false);
    if (this.pinch > 0) this.zoomBy(distance / this.pinch);
    this.pinch = distance;
  }

  private show(label: string, content: ZoomContent): void {
    this.reset();
    this.label.set(label);
    this.content.set(content);
    this.open.set(true);
    document.body.style.overflow = 'hidden';
  }

  close(): void {
    if (!this.open.get()) return;
    this.open.set(false);
    this.dragging.set(false);
    document.body.style.overflow = '';
    const request = this.request;
    setTimeout(() => {
      if (!this.open.get() && request === this.request) this.content.set(null);
    }, CLOSE_MS);
  }

  /** An SVG already on the page, copied so it scales freely in the view. */
  openInline(svg: SVGElement, label: string): void {
    this.request += 1;
    const clone = svg.cloneNode(true);
    if (!(clone instanceof SVGElement)) return;
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    clone.classList.add('docs-zoom-svg', 'docs-zoom-railroad-svg');
    this.show(label || 'Diagram', { state: 'ready', svg: clone });
  }

  /** An SVG by address: fetched, parsed, and freed of its fixed size so it scales. */
  async openUrl(url: string, label: string): Promise<void> {
    const request = ++this.request;
    this.show(label || 'Diagram', { state: 'loading' });
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const svg = new DOMParser().parseFromString(await response.text(), 'image/svg+xml').querySelector('svg');
      if (!svg) throw new Error('No <svg> element in response');
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.style.cssText = '';
      svg.classList.add('docs-zoom-svg');
      if (request === this.request) this.content.set({ state: 'ready', svg });
    } catch (error) {
      if (request === this.request) this.content.set({ state: 'error', message: error instanceof Error ? error.message : String(error) });
    }
  }
}
